/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  AwvInfo,
  CameraApi,
  CommandContentCamera,
  ManageCameraSpecificationsParam,
  WebrtcInfo,
} from "client";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ControlPanel } from "components/Camera/ControlPanel";
import { WebRTCViewer } from "components/Camera/WebRTCViewer";
import { Box, styled } from "@mui/material";
import { Const } from "const";
import { SpinnerBack } from "components/Common/SpinnerBack";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import {
  SystemErrorDialog,
  SystemErrorDialogProps,
} from "components/Common/SystemErrorDialog";
import { WebRTCConnection, shouldConnect } from "utils/WebRtc";
import { formatDate, getElapsedTime } from "utils/DateTime";
import { t } from "i18next";
import { ErrorDialog } from "components/Common/ErrorDialog";

type ConnectionStatus =
  | "Cannot connect"
  | "Not connected"
  | "connected"
  | "failed";
/**
 * Component of camera stream
 * @return{FC} Component
 */
export const Camera: FC = () => {
  const [connection, setConnection] = useState<WebRTCConnection>();
  const [currentCamera, setCurrentCamera] =
    useState<ManageCameraSpecificationsParam>();
  const [loading, setLoading] = useState(false);
  const [cameraApi, cancelCamera] = useApiWithTimeout(
    CameraApi,
    Const.operationTimeout.cameraTask
  );
  const [showApiError, setShowApiError] = useState<SystemErrorDialogProps>();
  const [showTimeoutError, setShowTimeoutError] = useState(false);

  const location = useLocation();
  const awvInfo = location.state ? (location.state as AwvInfo) : undefined;
  console.debug(awvInfo);
  const cameras = awvInfo?.cameraSpecifications;

  const connectionStatusRef = useRef<ConnectionStatus>("Not connected");

  // WebRTC connection process start time.
  const startTimeRef = useRef<Date>(new Date());

  // Polling interval ID
  const intervalId = useRef(0);

  const commandContent = useRef({
    webrtcResolution: 0,
    webrtcFrameRate: 0,
    webrtcEnable: 0,
    webrtcSelect: 0,
  } as CommandContentCamera);

  const navigate = useNavigate();

  /**
   * Display error dialog for API error
   * @param{ResponseError} error Error object
   * @param{func} onClosed Action on after message dialog closed
   */
  const raiseApiError = useCallback(
    (error: unknown | undefined, onClosed?: () => void) => {
      if (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          // Ignore abort errors
          console.debug("ignore");
          return;
        }
        console.error(error);
        setShowApiError({
          open: true,
          error: error,
          onClose: () => {
            setShowApiError(undefined);
            onClosed?.();
          },
        });
      }
    },
    []
  );

  /**
   * Query camera connection info of AWV
   * @param {string} frameTypeAndNumber Frame type and number of AWV
   */
  const queryConnection = useCallback(
    async (frameTypeAndNumber: string) => {
      try {
        const webRtc = await cameraApi.getWebrtcInfo({ frameTypeAndNumber });
        console.debug("webRtc: %o", webRtc);
        return createConnectionInfo(webRtc);
      } catch (error) {
        raiseApiError(error);
      }
    },
    [cameraApi, raiseApiError]
  );

  // Effect for initialize
  useEffect(() => {
    // Initialize component
    const initialize = async () => {
      if (awvInfo?.frameTypeAndNumber) {
        // Set initial current camera
        const firstCamera = cameras?.find(
          (c) => c.cameraNumber === Const.cameraFrontLeft
        );
        setCurrentCamera(firstCamera);
      } else {
        navigate(Const.path.root);
      }
    };

    initialize();

    return () => {
      cancelCamera();
      setCurrentCamera(undefined);
    };
  }, [awvInfo?.frameTypeAndNumber, cameras, cancelCamera, navigate]);

  const updateConnection = (connectionInfo?: WebRTCConnection) => {
    console.info("updateConnection");
    setConnection(connectionInfo ? { ...connectionInfo } : connectionInfo);
  };

  const startPolling = useCallback((webRtc?: WebRTCConnection) => {
    intervalId.current = window.setInterval(() => {
      console.debug(`[${formatDate(new Date(), "Y-MM-DD HH:mm:ss")}]Polling`);
      console.debug(`connectionStatusRef: ${connectionStatusRef.current}`);
      if (connectionStatusRef.current !== "connected") {
        // Check for timed out
        if (
          getElapsedTime(startTimeRef.current) <
          Const.operationTimeout.webRtcConnect
        ) {
          // retry if not timed out
          updateConnection(webRtc);
        } else {
          // Timed out
          setLoading(false);
          setShowTimeoutError(true);
          window.clearInterval(intervalId.current);
        }
      } else {
        // Stop retry if connection succeed
        window.clearInterval(intervalId.current);
      }
    }, Const.webRtcConnectInterval);
  }, []);

  // Effect for change camera
  useEffect(() => {
    console.debug("currentCamera: %o", currentCamera);
    const func = async () => {
      if (awvInfo?.frameTypeAndNumber && currentCamera) {
        setLoading(true);
        // Retrieve connection setting.
        const webRtc = await queryConnection(awvInfo.frameTypeAndNumber);

        if (shouldConnect(webRtc)) {
          // Put streaming setting
          commandContent.current.webrtcSelect = currentCamera.cameraNumber;
          if (connectionStatusRef.current !== "connected") {
            commandContent.current.webrtcResolution = webRtc?.resolution;
            commandContent.current.webrtcFrameRate = webRtc?.frameRate;
            commandContent.current.webrtcEnable = Const.webRtcEnableStart;
          }
          await cameraApi.settingWebrtc({
            bodyCameraSetting: {
              frameTypeAndNumber: awvInfo.frameTypeAndNumber,
              commandContentCamera: commandContent.current,
            },
          });

          // Command content for camera switch
          commandContent.current.webrtcResolution = 0;
          commandContent.current.webrtcFrameRate = 0;
          commandContent.current.webrtcEnable = Const.webRtcEnableGoing;

          // Initialize connection status
          startTimeRef.current = new Date();
          connectionStatusRef.current = "Not connected";

          // Start WebRTC Connecting.
          updateConnection(webRtc);

          // Start polling
          startPolling(webRtc);
        } else {
          // Just set connection info to display error
          setConnection(webRtc);
          setLoading(false);
        }
      }
    };
    func().catch((reason) => {
      console.error("reason: %o", reason);
      setLoading(false);
      raiseApiError(reason);
    });
  }, [
    currentCamera,
    awvInfo?.frameTypeAndNumber,
    cameraApi,
    raiseApiError,
    queryConnection,
    startPolling,
  ]);

  /**
   * Create connection info object from API response
   * @param {WebrtcInfo} response API response
   * @return {WebRTCConnection} WebRTC connection info
   */
  const createConnectionInfo: (response: WebrtcInfo) => WebRTCConnection = (
    response
  ) => {
    return {
      status: response.webrtcStatus,
      frameRate: response.webrtcFrameRate ?? 0,
      resolution: response.webrtcResolution ?? 0,
      accessKeyId: response.accessKeyId ?? "",
      secretAccessKey: response.secretAccessKey ?? "",
      sessionToken: response.sessionToken ?? "",
      channelArn: response.channelArn ?? "",
    };
  };

  /**
   * Callback for WebRTC connection succeed
   */
  const handleConnected = useCallback(() => {
    console.info("handleConnected");
    console.debug(`connectionStatusRef: ${connectionStatusRef.current}`);
    connectionStatusRef.current = "connected";
  }, []);

  /**
   * Callback for Video started.
   */
  const handleVideoStarted = useCallback(async () => {
    try {
      if (awvInfo?.frameTypeAndNumber) {
        // Set WebRTC user
        await cameraApi.settingWebrtcUser({
          frameTypeAndNumber: awvInfo.frameTypeAndNumber,
        });
      }
    } catch (error) {
      raiseApiError(error);
    } finally {
      setLoading(false);
    }
  }, [awvInfo?.frameTypeAndNumber, cameraApi, raiseApiError]);

  const handleConnectionFailed = useCallback(
    (error: unknown) => {
      connectionStatusRef.current = "failed";
      raiseApiError(error);
      // Stop spinner
      setLoading(false);
    },
    [raiseApiError]
  );
  const onCameraSelected = async (cameraNo: number) => {
    console.debug(`cameraNo: ${cameraNo}`);
    console.debug(`cameras: ${cameras}`);
    if (cameras && connectionStatusRef.current === "connected") {
      const selectedCamera = cameras.find((c) => c.cameraNumber === cameraNo);
      setCurrentCamera(selectedCamera);
    }
  };

  /**
   * Action for back
   */
  const back = () => {
    if (
      awvInfo?.frameTypeAndNumber &&
      shouldConnect(connection) &&
      currentCamera
    ) {
      // Stop streaming and back to previous screen

      // Start loading
      setLoading(true);

      // Request stop streaming
      cameraApi
        .settingWebrtc({
          bodyCameraSetting: {
            frameTypeAndNumber: awvInfo.frameTypeAndNumber,
            commandContentCamera: {
              webrtcEnable: Const.webRtcEnableStop,
              webrtcSelect: currentCamera.cameraNumber,
            },
          },
        })
        .then((requestId) => {
          // Back to previous screen
          navigate(-1);
        })
        .catch((reason) => {
          console.error("reason: %o", reason);
          raiseApiError(reason);
        })
        .finally(() => {
          // Stop spinner
          setLoading(false);
        });
    } else {
      // Just back to previous screen
      navigate(-1);
    }
  };
  return (
    <>
      {awvInfo !== undefined && (
        <Container>
          <ViewerArea>
            <WebRTCViewer
              connection={connection}
              onConnected={handleConnected}
              onConnectionFailed={handleConnectionFailed}
              onVideoStarted={handleVideoStarted}
            />
          </ViewerArea>
          <ControlArea>
            <ControlPanel
              currentCameraNo={currentCamera?.cameraNumber}
              cameraName={currentCamera?.cameraName}
              onCameraChanged={onCameraSelected}
              onBackClicked={back}
            />
          </ControlArea>
        </Container>
      )}
      {showApiError && (
        <SystemErrorDialog {...showApiError} open={!!showApiError} />
      )}
      <SpinnerBack open={loading} />
      <ErrorDialog
        open={showTimeoutError}
        onPositiveButtonClick={() => {
          setShowTimeoutError(false);
        }}
        message={t("AWVUI-MSG175")}
        positiveButtonText={t("AWVUI-MSG22")}
      />
    </>
  );
};

const Container = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: "grid",
  gridTemplateRows: "100%",
  gridTemplateColumns: "auto 350px",
  gap: theme.spacing(1, 0),
}));

const ViewerArea = styled(Box)({
  gridRow: "1",
  gridColumn: "1",
  display: "flex",
  position: "relative",
  flexDirection: "column",
  justifyContent: "center",
});

const ControlArea = styled(Box)({
  gridRow: "1",
  gridColumn: "2",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
});
